import { Stack, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { useParams } from "react-router-dom";
import { PageLayout } from "../../components";
import { CustomTabs } from "../../components/CustomTabs";
import { useCheckAreaAccess } from "../../hooks/useCheckAreaAccess";
import { useLocalization } from "../../hooks/useLocalization";
import { useTabNavigation } from "../../hooks/useTabNavigation";
import { theme } from "../../theme";
import { ELicenseTabs } from "../../types/NavigationPaths";
import { OverviewTab } from "./components/tabs/OverviewTab";
import { WhitelistTab } from "./components/tabs/WhitelistTab";
import { useLicenseById } from "./hooks/useLicenseById";
import { useWhitelistByLicenseId } from "./hooks/useWhitelistByLicenseId";
import { useIsWhitelistEnabled } from "./hooks/useWhitelistFeatureFlag";

export const ExternalLicenseDetailsPage: FC = () => {
  const { licenseId } = useParams<{ licenseId: string }>();
  const { t } = useLocalization();
  const isWhitelistEnabled = useIsWhitelistEnabled();
  const { companyId } = useParams();
  const { isLoading: isCheckAccessLoading, access } = useCheckAreaAccess(companyId);
  const areHooksEnabled = !!companyId && !isCheckAccessLoading && access.license;

  const { license, isLoading } = useLicenseById({ licenseId, enabled: areHooksEnabled });


  const showWhitelistTab = isWhitelistEnabled && Boolean(license?.hasWhitelist);

  const { data: whitelist, isLoading: isWhitelistLoading } =
    useWhitelistByLicenseId({ licenseId, enabled: showWhitelistTab });

  const tabLabels = useMemo(
    () => ({
      [ELicenseTabs.OVERVIEW]: t("licenses.tabs.overview"),
      [ELicenseTabs.WHITELIST]: t("licenses.tabs.whitelist"),
    }),
    [t],
  );

  const { activeTab, handleTabChange, tabItems } =
    useTabNavigation<ELicenseTabs>({
      tabEnum: ELicenseTabs,
      defaultTab: ELicenseTabs.OVERVIEW,
      enabled: showWhitelistTab,
      labels: tabLabels,
    });

  return (
    <PageLayout isLoading={isLoading || isWhitelistLoading} maxWidth={false}>
      <Stack spacing={{ xs: 1, md: 2 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { sx: 18, md: 24 },
            mb: 4,
            color: theme.colors.text[5],
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {license?.experienceName}
        </Typography>

        {showWhitelistTab && (
          <CustomTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabs={tabItems}
          />
        )}

        {activeTab === ELicenseTabs.OVERVIEW && (
          <OverviewTab license={license} />
        )}

        {activeTab === ELicenseTabs.WHITELIST && showWhitelistTab && (
          <WhitelistTab license={license} whitelist={whitelist} />
        )}
      </Stack>
    </PageLayout>
  );
};
